/**
 * Alerts Page
 *
 * Main page for viewing and managing backup alerts.
 *
 * Requirements:
 * - FR-035: View list of alerts with filtering and sorting
 * - FR-036: Acknowledge and resolve alerts
 * - FR-037: Alert details and history
 *
 * Conformed to Spec 197 design blueprint
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertSourceLink } from '../../../utils/alertSourceLink';
import {
  RefreshCw,
  Eye,
  Check,
  AlertCircle,
  Clock,
  AlertTriangle,
  FileText,
  ChevronUp,
} from 'lucide-react';
import { AlertDetailModal } from '../components/alerts/AlertDetailModal';
import { alertsApi, Alert, AlertSummary as AlertSummaryType, AlertAnalytics, ActorCount } from '../api/alerts';
import { AlertFilters as AlertFiltersType } from '../hooks/useAlerts';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Select,
  Checkbox,
  Badge,
  StatCard,
  ErrorState,
  LoadingState,
  Extended,
  QuickGlance,
  AppTooltip,
  PageHelpPanel,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import type { BadgeVariant } from '../../../components/ui';
import { PaginationControls } from '../../../components/operations/PaginationControls';
import { systemHelp } from '../../../data/help/systemHelp';
import { formatDistanceToNow } from 'date-fns';
import { clsx } from 'clsx';

/** 'YYYY-MM-DD' → 'M/D' for the severity-history axis. */
const fmtDay = (d: string): string => {
  const dt = new Date(`${d}T00:00:00`);
  return `${dt.getMonth() + 1}/${dt.getDate()}`;
};

/** A compact "name — count" bar list for the per-user owner / resolver breakdowns. */
const ActorBars: React.FC<{ title: string; rows: ActorCount[]; colorVar: string; emptyText: string }> = ({
  title,
  rows,
  colorVar,
  emptyText,
}) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
        {title}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-[var(--color-text-secondary)]">{emptyText}</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.slice(0, 5).map((r) => (
            <li key={r.userId}>
              <div className="flex items-center justify-between text-xs">
                <span className="truncate text-[var(--color-text-primary)]">{r.name}</span>
                <span className="ml-2 font-semibold tabular-nums text-[var(--color-text-primary)]">{r.count}</span>
              </div>
              <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-bg-subtle)]">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${(r.count / max) * 100}%`, background: `var(${colorVar})` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const Alerts: React.FC = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [summary, setSummary] = useState<AlertSummaryType | null>(null);
  const [analytics, setAnalytics] = useState<AlertAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAlertIds, setSelectedAlertIds] = useState<string[]>([]);

  // Pagination — server-side (the list can be large); the DataTable's built-in pager is
  // disabled so this is the single source of truth for page + page size.
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10); // design blueprint anchor: 10

  // Filters
  const [filters, setFilters] = useState<AlertFiltersType>({
    lakehouseId: undefined,
    severity: undefined,
    alertType: undefined,
    isAcknowledged: undefined,
    isResolved: undefined,
  });

  // Sorting
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await alertsApi.getAlerts({
        ...filters,
        page,
        limit: pageSize,
        sortBy,
        sortOrder,
      });

      setAlerts(result.items);
      setTotalPages(result.totalPages);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, sortBy, sortOrder]);

  const fetchSummary = useCallback(async () => {
    try {
      const summaryData = await alertsApi.getSummary();
      setSummary(summaryData);
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const data = await alertsApi.getAnalytics(14);
      setAnalytics(data);
    } catch (err) {
      console.error('Failed to load alert analytics:', err);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetchSummary();
    fetchAnalytics();
  }, [fetchAlerts, fetchSummary, fetchAnalytics]);

  // True multi-user: poll so another operator's acknowledge/resolve becomes visible HERE
  // within ~15s — and immediately when the tab regains focus — with no manual refresh. Keeps
  // every viewer's list + status tiles in lock-step so two people never work the same alert
  // blind to each other (acknowledging = taking ownership, and everyone sees it).
  useEffect(() => {
    const tick = (): void => {
      void fetchAlerts();
      void fetchSummary();
      void fetchAnalytics();
    };
    const interval = setInterval(tick, 15_000);
    const onFocus = (): void => {
      if (document.visibilityState === 'visible') tick();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [fetchAlerts, fetchSummary, fetchAnalytics]);

  const handleSort = (field: string): void => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handlePageSizeChange = (size: number): void => {
    setPageSize(size);
    setPage(1); // avoid landing on a now-out-of-range page
  };

  const handleViewDetails = (alert: Alert): void => {
    setSelectedAlert(alert);
    setShowDetailModal(true);
  };

  const handleAcknowledge = async (alertId: string, notes?: string): Promise<void> => {
    try {
      await alertsApi.acknowledge(alertId, notes);
      await fetchAlerts();
      await fetchSummary();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge alert');
    }
  };

  const handleBulkAcknowledge = async (): Promise<void> => {
    if (selectedAlertIds.length === 0) {
      return;
    }

    try {
      await alertsApi.bulkAcknowledge(selectedAlertIds);
      setSelectedAlertIds([]);
      await fetchAlerts();
      await fetchSummary();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge alerts');
    }
  };

  const handleBulkResolve = async (resolution: string): Promise<void> => {
    if (selectedAlertIds.length === 0) {
      return;
    }

    try {
      await alertsApi.bulkResolve(selectedAlertIds, resolution);
      setSelectedAlertIds([]);
      await fetchAlerts();
      await fetchSummary();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve alerts');
    }
  };

  const handleSelectAlert = (alertId: string, selected: boolean): void => {
    if (selected) {
      setSelectedAlertIds([...selectedAlertIds, alertId]);
    } else {
      setSelectedAlertIds(selectedAlertIds.filter((id) => id !== alertId));
    }
  };

  const handleSelectAll = (selected: boolean): void => {
    if (selected) {
      setSelectedAlertIds(alerts.map((a) => a.id));
    } else {
      setSelectedAlertIds([]);
    }
  };

  const handleRefresh = (): void => {
    fetchAlerts();
    fetchSummary();
  };

  // ----------------------------------------------------------------------------
  // Token-driven status helpers (replace the old raw-gray className maps)
  // ----------------------------------------------------------------------------
  const severityVariant = (severity: string): BadgeVariant => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'warning';
      // Owner 2026-06-11: WARNING = yellow, INFO = green
      case 'warning':
        return 'warning';
      case 'info':
        return 'success';
      case 'low':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const statusVariant = (status: string): BadgeVariant => {
    switch (status) {
      case 'active':
        return 'error';
      case 'acknowledged':
        return 'warning';
      case 'resolved':
        return 'success';
      default:
        return 'secondary';
    }
  };

  // mapFromDatabase derives status='active' for unresolved+unacknowledged rows; label it
  // "Unacknowledged" so the badge matches the summary tile + Status filter terminology.
  const statusLabel = (status: string): string =>
    status === 'active'
      ? 'Unacknowledged'
      : status.charAt(0).toUpperCase() + status.slice(1);

  const alertTypeIcon = (alertType: string): React.ReactNode => {
    switch (alertType) {
      case 'backup_failed':
        return <AlertCircle className="h-4 w-4 text-[var(--color-status-danger)]" />;
      case 'backup_overdue':
        return <Clock className="h-4 w-4 text-[var(--color-status-warning)]" />;
      case 'backup_warning':
        return <AlertTriangle className="h-4 w-4 text-[var(--color-status-warning)]" />;
      default:
        return <AlertCircle className="h-4 w-4 text-[var(--color-text-secondary)]" />;
    }
  };

  const formatAlertType = (type: string | undefined): string => {
    if (!type) return 'Unknown';
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // "storage_account" -> "Storage Account" for the Source column type label.
  const humanizeEntityType = (t?: string | null): string =>
    (t ?? '')
      .split('_')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  // ----------------------------------------------------------------------------
  // Filter handlers (preserve the original status<->ack/resolved encoding)
  // ----------------------------------------------------------------------------
  const statusFilterValue =
    filters.isResolved === undefined
      ? ''
      : filters.isResolved
        ? 'resolved'
        : filters.isAcknowledged
          ? 'acknowledged'
          : 'unacknowledged';

  const handleStatusFilter = (value: string): void => {
    if (value === '') {
      setFilters((prev) => ({ ...prev, isAcknowledged: undefined, isResolved: undefined }));
    } else if (value === 'unacknowledged') {
      setFilters((prev) => ({ ...prev, isAcknowledged: false, isResolved: false }));
    } else if (value === 'acknowledged') {
      setFilters((prev) => ({ ...prev, isAcknowledged: true, isResolved: false }));
    } else if (value === 'resolved') {
      setFilters((prev) => ({ ...prev, isResolved: true }));
    }
    setPage(1);
  };

  const handleFieldFilter = (field: keyof AlertFiltersType, value: string): void => {
    setFilters((prev) => ({ ...prev, [field]: value || undefined }));
    setPage(1);
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== undefined);

  const clearFilters = (): void => {
    setFilters({
      lakehouseId: undefined,
      severity: undefined,
      alertType: undefined,
      isAcknowledged: undefined,
      isResolved: undefined,
      acknowledgedBy: undefined,
      resolvedBy: undefined,
    });
    setPage(1);
  };

  // ----------------------------------------------------------------------------
  // Summary tile config — multi-user ownership KPIs
  // ----------------------------------------------------------------------------
  // Disjoint status partition — Unattended + Owned + Resolved = Total. Each tile equals its
  // Status-filter bucket ("Unattended" = the unacknowledged / bell "needs attention" number),
  // reframed around OWNERSHIP so it's immediately apparent who is (and isn't) on each alert.
  const oldestUnattended = summary?.oldestUnattendedAt
    ? formatDistanceToNow(new Date(summary.oldestUnattendedAt), { addSuffix: true })
    : null;
  const ownerCount = summary?.activeOwners ?? 0;
  const summaryTiles: Array<{
    label: string;
    value: number;
    icon: React.ReactNode;
    valueClassName?: string;
    subtext?: React.ReactNode;
    tooltip?: string;
  }> = summary
    ? [
        {
          label: 'Unattended',
          value: summary.unacknowledged,
          icon: <AlertCircle className="h-4 w-4" />,
          valueClassName: 'text-[var(--color-status-danger)]',
          subtext:
            summary.unacknowledged > 0 && oldestUnattended
              ? `oldest ${oldestUnattended}`
              : 'every alert has an owner',
          tooltip: 'Open alerts nobody has acknowledged yet — no one has taken ownership.',
        },
        {
          label: 'Owned',
          value: summary.acknowledged,
          icon: <Eye className="h-4 w-4" />,
          valueClassName: 'text-[var(--color-status-warning)]',
          subtext: `${ownerCount} ${ownerCount === 1 ? 'person' : 'people'} handling`,
          tooltip: 'Acknowledged but not yet resolved — someone has taken ownership and is working them.',
        },
        {
          label: 'Resolved',
          value: summary.resolved,
          icon: <Check className="h-4 w-4" />,
          valueClassName: 'text-[var(--color-status-success)]',
          subtext: `${summary.resolvedLast24h ?? 0} cleared in 24h`,
          tooltip: 'Cleared alerts (all time). The subtext shows how many were cleared in the last 24 hours.',
        },
        {
          label: 'Total',
          value: summary.total,
          icon: <FileText className="h-4 w-4" />,
        },
      ]
    : [];

  // ----------------------------------------------------------------------------
  // Table columns
  // ----------------------------------------------------------------------------
  const allSelected = alerts.length > 0 && alerts.every((a) => selectedAlertIds.includes(a.id));
  const someSelected = selectedAlertIds.length > 0 && !allSelected;

  const SortHeader: React.FC<{ field: string; label: string }> = ({ field, label }) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className="inline-flex items-center gap-1 font-semibold text-[var(--color-text-primary)] hover:text-[var(--color-accent)]"
    >
      {label}
      {sortBy === field && (
        <ChevronUp className={clsx('h-3.5 w-3.5 transition-transform', sortOrder === 'desc' && 'rotate-180')} />
      )}
    </button>
  );

  const columns: DataTableColumn<Alert>[] = [
    {
      key: 'select',
      header: (
        <Checkbox
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = someSelected;
          }}
          onChange={(e) => handleSelectAll(e.target.checked)}
          aria-label="Select all alerts"
        />
      ),
      width: '4%',
      render: (a) => (
        <Checkbox
          checked={selectedAlertIds.includes(a.id)}
          onChange={(e) => {
            e.stopPropagation();
            handleSelectAlert(a.id, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select alert ${a.id}`}
        />
      ),
    },
    {
      key: 'alert_type',
      header: <SortHeader field="alert_type" label="Type" />,
      width: '16%',
      render: (a) => (
        <div className="flex min-w-0 items-center gap-2">
          {alertTypeIcon(a.alertType || a.type || '')}
          <span className="truncate font-medium text-[var(--color-text-primary)]">
            {formatAlertType(a.alertType || a.type)}
          </span>
        </div>
      ),
    },
    {
      key: 'severity',
      header: <SortHeader field="severity" label="Severity" />,
      width: '10%',
      render: (a) => (
        <Badge variant={severityVariant(a.severity || 'medium')}>
          {(a.severity || 'medium').toUpperCase()}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: <SortHeader field="status" label="Status" />,
      width: '13%',
      render: (a) => (
        <div className="min-w-0">
          <Badge variant={statusVariant(a.status || 'active')}>
            {statusLabel(a.status || 'active')}
          </Badge>
          {/* Spec 255: who acted + when — resolver takes precedence over acknowledger */}
          {a.resolvedAt ? (
            <div className="truncate text-[11px] text-[var(--color-text-secondary)]"
              title={`Resolved by ${a.resolvedByName ?? a.resolvedBy ?? 'system'}`}>
              by {a.resolvedByName ?? 'system'} · {formatDistanceToNow(new Date(a.resolvedAt), { addSuffix: true })}
            </div>
          ) : a.acknowledgedAt ? (
            <div className="truncate text-[11px] text-[var(--color-text-secondary)]"
              title={`Acknowledged by ${a.acknowledgedByName ?? a.acknowledgedBy ?? 'unknown'}`}>
              by {a.acknowledgedByName ?? 'unknown'} · {formatDistanceToNow(new Date(a.acknowledgedAt), { addSuffix: true })}
            </div>
          ) : (
            <div className="truncate text-[11px] font-medium text-[var(--color-status-danger)]"
              title="No one has acknowledged this alert yet">
              no owner yet
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      width: '17%',
      render: (a) => {
        const typeLabel = humanizeEntityType(a.entityType);
        if (a.entityName) {
          return (
            <span className="inline-flex min-w-0 items-center gap-1.5">
              {typeLabel && (
                <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">
                  {typeLabel}:
                </span>
              )}
              <span className="truncate text-[var(--color-text-primary)]">{a.entityName}</span>
              {(a.connectionCount ?? 0) > 1 && (
                <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">
                  +{(a.connectionCount ?? 1) - 1}
                </span>
              )}
            </span>
          );
        }
        // Unresolvable id (synthetic pipeline id, un-collected storage account): show the
        // entity kind rather than a raw GUID or a blank — the specific name is in the title.
        return (
          <span className="text-[var(--color-text-secondary)]">{typeLabel || '—'}</span>
        );
      },
    },
    {
      key: 'message',
      header: 'Message',
      width: '22%',
      render: (a) => (
        <span className="truncate text-[var(--color-text-secondary)]" title={a.message}>
          {a.message}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: <SortHeader field="created_at" label="Created" />,
      width: '11%',
      render: (a) => (
        <span className="text-[var(--color-text-secondary)]">
          {formatDistanceToNow(new Date(a.createdAt), { addSuffix: true })}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: '9%',
      render: (a) => (
        <div className="flex items-center justify-end gap-1">
          <AppTooltip title="View Details">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(a);
              }}
            >
              <Eye className="h-4 w-4" />
            </Button>
          </AppTooltip>
          {a.status === 'active' && (
            <AppTooltip title="Acknowledge">
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAcknowledge(a.id);
                }}
              >
                <Check className="h-4 w-4 text-[var(--color-status-warning)]" />
              </Button>
            </AppTooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <PageLayout
      title="Alerts"
      description="All fired alerts across the platform — capacities, backups, and infrastructure. Review, acknowledge, and resolve."
      actions={
        <>
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-bg-subtle)] px-2.5 py-1 text-xs font-medium text-[var(--color-text-secondary)]"
            title="This page updates automatically — any user's acknowledge or resolve appears here within ~15 seconds (and instantly when you return to the tab)."
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
            </span>
            Live
          </span>
          <PageHelpPanel content={systemHelp} buttonSize="sm" />
          <AppTooltip title="Refresh alerts list and summary data">
            <Button
              variant="primary"
              leftIcon={<RefreshCw className={clsx('h-4 w-4', loading && 'animate-spin')} />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </AppTooltip>
        </>
      }
    >
      {/* Error */}
      {error && (
        <div className="mb-6">
          <ErrorState title="Failed to load alerts" message={error} onRetry={handleRefresh} />
        </div>
      )}

      {/* Summary tiles */}
      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {summaryTiles.map((tile) => (
            <StatCard
              key={tile.label}
              label={tile.label}
              value={tile.value}
              icon={tile.icon}
              valueClassName={tile.valueClassName}
              subtext={tile.subtext}
              tooltip={tile.tooltip}
            />
          ))}
        </div>
      )}

      <QuickGlance>
        <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
          Showing the at-a-glance summary. Open this page on a larger screen for the full details.
        </Card>
      </QuickGlance>

      <Extended>
      {/* Multi-user analytics — severity trend over time + who owns / who clears */}
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Alerts-by-severity history (spans two columns) */}
        <Card className="p-4 lg:col-span-2">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-[var(--color-text-primary)]">
              Alerts by severity — last 14 days
            </h3>
            {summary?.bySeverity && (
              <div className="flex flex-wrap gap-3 text-xs">
                {Object.entries(summary.bySeverity).map(([severity, count]) => (
                  <span key={severity} className="inline-flex items-center gap-1">
                    <Badge variant={severityVariant(severity)}>{severity.toUpperCase()}</Badge>
                    <span className="font-semibold tabular-nums text-[var(--color-text-primary)]">
                      {count as number}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.severityHistory ?? []} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={fmtDay}
                  tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  tickLine={false}
                  axisLine={{ stroke: 'var(--color-border)' }}
                />
                <YAxis
                  allowDecimals={false}
                  width={30}
                  tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }}
                  tickLine={false}
                  axisLine={false}
                />
                <RechartsTooltip
                  contentStyle={{
                    background: 'var(--color-bg-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: 'var(--color-text-primary)', fontWeight: 600 }}
                  labelFormatter={(d) => fmtDay(String(d))}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="critical" stackId="s" name="Critical" fill="var(--color-status-danger)" />
                <Bar dataKey="warning" stackId="s" name="Warning" fill="var(--color-status-warning)" />
                <Bar dataKey="info" stackId="s" name="Info" fill="var(--color-accent)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Who's on it — per-user owner + resolver breakdown */}
        <Card className="flex flex-col gap-4 p-4">
          <h3 className="text-sm font-medium text-[var(--color-text-primary)]">Who&apos;s on it</h3>
          <ActorBars
            title="Owned by (open)"
            rows={analytics?.owners ?? []}
            colorVar="--color-status-warning"
            emptyText="No open alerts have an owner yet."
          />
          <ActorBars
            title="Cleared by (all time)"
            rows={analytics?.resolvers ?? []}
            colorVar="--color-status-success"
            emptyText="Nothing has been cleared yet."
          />
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
          <div className="md:col-span-3">
            <Select
              isFullWidth
              label="Status"
              value={statusFilterValue}
              onChange={(e) => handleStatusFilter(e.target.value)}
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'unacknowledged', label: 'Unacknowledged' },
                { value: 'acknowledged', label: 'Acknowledged' },
                { value: 'resolved', label: 'Resolved' },
              ]}
            />
          </div>
          <div className="md:col-span-3">
            <Select
              isFullWidth
              label="Severity"
              value={filters.severity || ''}
              onChange={(e) => handleFieldFilter('severity', e.target.value)}
              options={[
                { value: '', label: 'All Severities' },
                { value: 'Critical', label: 'Critical' },
                { value: 'Warning', label: 'Warning' },
                { value: 'Info', label: 'Info' },
                { value: 'Low', label: 'Low' },
              ]}
            />
          </div>
          <div className="md:col-span-3">
            <Select
              isFullWidth
              label="Type"
              value={filters.alertType || ''}
              onChange={(e) => handleFieldFilter('alertType', e.target.value)}
              options={[
                { value: '', label: 'All Types' },
                { value: 'CapacityIssue', label: 'Capacity Issue' },
                { value: 'CapacityThrottling', label: 'Capacity Throttling' },
                { value: 'CollectionTierFailure', label: 'Collection Tier Failure' },
                { value: 'PipelineHealth', label: 'Pipeline Health' },
                { value: 'PredictabilityFinding', label: 'Predictability Finding' },
                { value: 'RecoverabilityFailed', label: 'Recoverability Failed' },
                { value: 'StorageThresholdBreach', label: 'Storage Threshold Breach' },
                { value: 'BackupRunFailed', label: 'Backup Run Failed' },
                { value: 'BackupRunPartial', label: 'Backup Run Partial' },
              ]}
            />
          </div>
          <div className="md:col-span-3">
            <Select
              isFullWidth
              label="Acknowledged by"
              value={filters.acknowledgedBy || ''}
              onChange={(e) => handleFieldFilter('acknowledgedBy', e.target.value)}
              options={[
                { value: '', label: 'Anyone' },
                ...(analytics?.acknowledgers ?? []).map((a) => ({
                  value: a.userId,
                  label: `${a.name} (${a.count})`,
                })),
              ]}
            />
          </div>
          <div className="md:col-span-3">
            <Select
              isFullWidth
              label="Resolved by"
              value={filters.resolvedBy || ''}
              onChange={(e) => handleFieldFilter('resolvedBy', e.target.value)}
              options={[
                { value: '', label: 'Anyone' },
                ...(analytics?.resolvers ?? []).map((a) => ({
                  value: a.userId,
                  label: `${a.name} (${a.count})`,
                })),
              ]}
            />
          </div>
          {hasActiveFilters && (
            <div className="flex md:col-span-3">
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Bulk actions */}
      {selectedAlertIds.length > 0 && (
        <Card className="mb-4 flex items-center justify-between p-4">
          <span className="text-sm font-medium text-[var(--color-text-primary)]">
            {selectedAlertIds.length} alert(s) selected
          </span>
          <div className="flex gap-2">
            <AppTooltip title="Mark selected alerts as acknowledged">
              <Button size="sm" variant="secondary" onClick={handleBulkAcknowledge}>
                Acknowledge Selected
              </Button>
            </AppTooltip>
            <Button
              size="sm"
              variant="success"
              onClick={() => {
                const resolution = prompt('Enter resolution for selected alerts:');
                if (resolution) {
                  handleBulkResolve(resolution);
                }
              }}
            >
              Resolve Selected
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedAlertIds([])}>
              Clear Selection
            </Button>
          </div>
        </Card>
      )}

      {/* Alerts table */}
      <Card padding="none">
        {loading ? (
          <div className="p-4">
            <LoadingState rows={6} />
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={alerts}
            rowKey={(a) => a.id}
            pageSize={pageSize}
            showPagination={false}
            minWidth="52rem"
            emptyMessage="No alerts match the current filter criteria."
            onRowClick={(a) => {
              // Spec 193: jump to the alert's source entity page. Row-action buttons
              // (view/acknowledge/checkbox) already stopPropagation, so they win.
              const url = alertSourceLink({
                entityType: a.entityType,
                entityId: a.entityId,
                resourceType: a.resourceType,
                // Spec 265 FR-025b: the stage decides WHICH integrity screen owns it.
                sourceScope: a.sourceScope,
              });
              if (url) navigate(url);
            }}
          />
        )}
      </Card>

      {/* Standard blueprint pagination (shared PaginationControls) — server-driven.
          Rendered whenever there are rows so the page-size selector is always available
          (not just when totalPages > 1, which would hide it at a large page size). */}
      {total > 0 && (
        <div className="mt-4">
          <PaginationControls
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}
      </Extended>

      {/* Alert Detail Modal */}
      {showDetailModal && selectedAlert && (
        <AlertDetailModal
          alertId={selectedAlert.id}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedAlert(null);
            // Reflect any acknowledge/resolve done inside the modal (this page fetches
            // the list/summary manually — it doesn't share the modal's react-query cache).
            fetchAlerts();
            fetchSummary();
          }}
        />
      )}
    </PageLayout>
  );
};

export default Alerts;
